import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { TicketsApiService } from '../services/tickets-api.service';
import { TicketsConfigApiService } from '../services/tickets-config-api.service';
import { Ticket } from '../models/tickets.model';
import {
  TicketCategory,
  TicketPipeline,
  TicketPipelineStage,
  TicketPriority,
  TicketRootCause,
  TicketStatus
} from '../models/tickets-config.model';
import { TicketsDetailTabsComponent } from '../tickets-detail-tabs/tickets-detail-tabs.component';
import {
  TicketActionType,
  TicketsActionsModalsComponent
} from '../tickets-actions-modals/tickets-actions-modals.component';

/**
 * Vista completa de un ticket en modal (INC 43). Además de las tabs, aloja la barra
 * de acciones (Escalar / Resolver / Reabrir / Sub-ticket / Fusionar / Dividir) y el
 * modal multi-acción, de modo que el modal es la vista única y completa del ticket.
 *
 * Al completar una acción refresca el ticket dentro del modal y emite `refetch` para
 * que el padre (lista / kanban) recargue y desaparezcan los fusionados (INC 30).
 */
@Component({
  selector: 'app-tickets-detail-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    TicketsDetailTabsComponent,
    TicketsActionsModalsComponent
  ],
  templateUrl: './tickets-detail-modal.component.html'
})
export class TicketsDetailModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() ticket: Ticket | null = null;

  @Input() priorities: TicketPriority[] = [];
  @Input() statuses: TicketStatus[] = [];
  @Input() categories: TicketCategory[] = [];
  @Input() pipelines: TicketPipeline[] = [];
  @Input() stages: TicketPipelineStage[] = [];

  @Output() close = new EventEmitter<void>();
  /** Se emite cuando una acción modifica el ticket (resolver/reabrir/escalar/fusionar/dividir)
   *  para que la lista/kanban vuelva a cargar (INC 30). */
  @Output() refetch = new EventEmitter<void>();

  @ViewChild(TicketsDetailTabsComponent) tabs?: TicketsDetailTabsComponent;

  /** Inc 73: true cuando el ticket mostrado es un subticket (tiene padre). */
  get esSubTicket(): boolean {
    return this.ticket?.idTicketParent != null;
  }

  /** Acción activa que controla el modal multi-acción. */
  activeAction: TicketActionType | null = null;
  /** Menú desplegable "Vínculos" de la barra de acciones. */
  linksMenuOpen = false;

  /** Causas raíz (necesarias para Resolver) — se cargan cuando se abre el modal. */
  rootCauses: TicketRootCause[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private ticketsApi: TicketsApiService,
    private configApi: TicketsConfigApiService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['isOpen'] || changes['ticket']) && this.isOpen && this.rootCauses.length === 0) {
      this.cargarRootCauses();
    }
    if (changes['isOpen'] && !this.isOpen) {
      this.activeAction = null;
      this.linksMenuOpen = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private cargarRootCauses(): void {
    this.configApi
      .getRootCauses()
      .pipe(catchError(() => of([] as TicketRootCause[])), takeUntil(this.destroy$))
      .subscribe((rc) => (this.rootCauses = rc));
  }

  onClose(): void {
    this.close.emit();
  }

  // ===== Visibilidad de acciones (mismo criterio que la página completa) =====
  canReopen(): boolean {
    if (!this.ticket?.idTicketStatus) return false;
    const s = this.statuses.find((x) => x.id === this.ticket?.idTicketStatus);
    return !!(s && (s.isResolved || s.isTerminal));
  }

  canResolve(): boolean {
    if (!this.ticket?.idTicketStatus) return true;
    const s = this.statuses.find((x) => x.id === this.ticket?.idTicketStatus);
    return !s || (!s.isResolved && !s.isTerminal);
  }

  // ===== Menú "Vínculos" =====
  toggleLinksMenu(): void {
    this.linksMenuOpen = !this.linksMenuOpen;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.linksMenuOpen) this.linksMenuOpen = false;
  }

  abrirAccion(action: TicketActionType): void {
    this.linksMenuOpen = false;
    this.activeAction = action;
  }

  cerrarAccion(): void {
    this.activeAction = null;
  }

  onAccionCompletada(updated: Ticket | null): void {
    const action = this.activeAction;
    this.activeAction = null;

    if (updated && action === 'sub-ticket') {
      // Inc 70: la lista/kanban debe recargar para que el nuevo sub-ticket aparezca
      // (bajo su padre) sin necesidad de F5.
      this.refetch.emit();
      // Inc 74: el usuario se queda en el ticket PADRE (antes se navegaba al hijo,
      // por lo que el evento no se veía en la línea de tiempo del padre).
      this.tabs?.refrescarTrasSubTicket();
      this.recargarTicket();
      return;
    }

    if (updated && (action === 'reopen' || action === 'merge')) {
      // Reopen y merge devuelven el ticket primario actualizado.
      this.ticket = updated;
    } else {
      // Resolve / Escalate / Split → recargar para tener el estado actualizado.
      this.recargarTicket();
    }
    // La lista/kanban debe refrescarse (fusionados desaparecen — INC 30).
    this.refetch.emit();
  }

  /**
   * Sub-ticket creado desde la pestaña "Vínculos".
   * Inc 74: se permanece en el ticket padre; la pestaña de tabs ya refrescó
   * vínculos e historial. Sólo se recarga la lista/kanban de fondo (Inc 70).
   */
  onSubTicketCreated(_child: Ticket): void {
    this.refetch.emit();
    this.recargarTicket();
  }

  private recargarTicket(): void {
    if (!this.ticket?.id) return;
    this.ticketsApi
      .getTicketById(this.ticket.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((t) => (this.ticket = t));
  }
}
